import { DashboardStats, CurrentWeekStats } from './types';

type SourceCounts = {
  paid: number;
  invited: number;
  orphan: number;
  community: number;
};

type Props = {
  stats: DashboardStats;
  currentWeek: CurrentWeekStats;
  sourceCounts?: SourceCounts;
};

function formatPct(v: number) {
  return `${v.toFixed(1)}%`;
}

function ChangeBadge({ value, format }: { value: number; format?: (v: number) => string }) {
  if (value === 0) {
    return <span class="text-gray-400 text-xs">no change</span>;
  }
  const display = format ? format(Math.abs(value)) : String(Math.abs(value));
  const isPositive = value > 0;
  return (
    <span
      class={`inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${
        isPositive ? 'bg-green-400/20 text-green-700' : 'bg-pink-400/15 text-pink-700'
      }`}
    >
      {isPositive ? '+' : '-'}
      {display}
    </span>
  );
}

export function HealthCards({ stats, currentWeek, sourceCounts }: Props) {
  const wauChange = currentWeek.wau - currentWeek.priorWeekWau;
  const visitorsChange = currentWeek.visitors - currentWeek.priorWeekVisitors;
  const engRateCurrent = currentWeek.engagementRate;
  const engRatePrior = currentWeek.priorEngagementRate;
  const engChange = Math.round((engRateCurrent - engRatePrior) * 10) / 10;
  const memberPostsChange = stats.memberPosts.current - stats.memberPosts.prior;

  return (
    <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
      <div>
        <hr role="presentation" class="w-full border-t" />
        <div class="mt-6 font-medium text-sm text-gray-500">Total Members</div>
        <div class="mt-3 text-2xl font-semibold">{currentWeek.totalMembers}</div>
        {sourceCounts && (
          <div class="mt-1 text-xs text-gray-600">
            <span class="font-medium text-gray-800">{sourceCounts.paid} paid</span>
            {' · '}
            {sourceCounts.invited} invited
            {sourceCounts.orphan > 0 && (
              <>
                {' · '}
                {sourceCounts.orphan} orphan
              </>
            )}
          </div>
        )}
        <div class="mt-3 text-xs flex flex-wrap gap-1">
          {currentWeek.newMembers > 0 && (
            <span class="bg-green-400/20 text-green-700 rounded-md px-1.5 py-0.5 font-medium">
              +{currentWeek.newMembers} new
            </span>
          )}
          {currentWeek.cancelledMembers > 0 && (
            <span class="bg-pink-400/15 text-pink-700 rounded-md px-1.5 py-0.5 font-medium">
              -{currentWeek.cancelledMembers} cancelled
            </span>
          )}
          {currentWeek.newMembers === 0 && currentWeek.cancelledMembers === 0 && (
            <span class="text-gray-400">no changes this week</span>
          )}
        </div>
      </div>

      <div>
        <hr role="presentation" class="w-full border-t" />
        <div class="mt-6 font-medium text-sm text-gray-500">Visitors (Mon–Sun)</div>
        <div class="mt-3 text-2xl font-semibold">{currentWeek.visitors}</div>
        <div class="mt-3 text-xs">
          <ChangeBadge value={visitorsChange} />
          <span class="text-gray-400 ml-1">vs prior week</span>
        </div>
      </div>

      <div>
        <hr role="presentation" class="w-full border-t" />
        <div class="mt-6 font-medium text-sm text-gray-500">WAU (Mon–Sun)</div>
        <div class="mt-3 text-2xl font-semibold">{currentWeek.wau}</div>
        <div class="mt-3 text-xs">
          <ChangeBadge value={wauChange} />
          <span class="text-gray-400 ml-1">vs prior week</span>
        </div>
      </div>

      <div>
        <hr role="presentation" class="w-full border-t" />
        <div class="mt-6 font-medium text-sm text-gray-500">Engagement Rate</div>
        <div class="mt-3 text-2xl font-semibold">{formatPct(engRateCurrent)}</div>
        <div class="mt-3 text-xs">
          <ChangeBadge value={engChange} format={formatPct} />
          <span class="text-gray-400 ml-1">vs prior week</span>
        </div>
      </div>

      <div>
        <hr role="presentation" class="w-full border-t" />
        <div class="mt-6 font-medium text-sm text-gray-500">Members Posting</div>
        <div class="mt-3 text-2xl font-semibold">{stats.memberPosts.current}</div>
        <div class="mt-3 text-xs">
          <ChangeBadge value={memberPostsChange} />
          <span class="text-gray-400 ml-1">vs prior period</span>
        </div>
      </div>
    </div>
  );
}
